import type { LoaderFunctionArgs } from "react-router";
import { useLoaderData } from "react-router";
import type { GraphQLError, IntrospectionQuery } from "graphql";
import { buildClientSchema } from "graphql";
import { sendGraphQLRequest } from "~/utils/graphql.server";
import type { GetHomeQuery, GetTournamentsQuery } from "~/@graphql/types";
import schema from "~/@graphql/schema.server.json";
import getGQLFile from "~/utils/get-gql-file.server";
import { getSession } from "~/session.server";
import Tournaments from "~/components/tournaments/Tournaments";
import LiveOnJiwe from "~/components/shared/LiveOnJiwe";
import { prepareManyTournamentsData } from "~/helpers/prepare-tournament-data";

type LoaderData = { data?: GetHomeQuery; errors?: GraphQLError[] };

export const loader = async (args: LoaderFunctionArgs) => {
  const session = await getSession(args.request, false);
  const token = session.get("accessToken");
  let query = getGQLFile("queries/get-home", ["tournaments"]);
  const authorization: string | undefined = `Bearer ${token}`;
  let headers:
    | {
      authorization: string;
    }
    | undefined = { authorization };
  if (!token) {
    query = getGQLFile("queries/get-home-anon", ["tournaments"]);
    headers = undefined;
  }
  const response = await sendGraphQLRequest({
    args,
    endpoint: process.env.GRAPHQL_API!,
    headers,
    schema: buildClientSchema(schema as unknown as IntrospectionQuery),
    query,
    variables: args.params,
  });
  if (response.ok) {
    let result = await response.json();
    return result
  }
  return { errors: [{ message: response.statusText }] }
};

export function meta() {
  return [{
    title: "Jiwe eSports",
  }];
}

export default function Index() {
  const { data, errors } = useLoaderData<LoaderData>() as unknown as LoaderData;

  if (errors) {
    return (
      <>
        {errors.map((err) => (
          <>{err.message}</>
        ))}
      </>
    ); // TODO: Handle errors better
  }

  const tournaments = prepareManyTournamentsData({
    tournaments: data?.tournaments as unknown as GetTournamentsQuery['tournaments'],
  }).sort((a, b) =>
    new Date(b.dates.tournamentDates.start).getTime() - new Date(a.dates.tournamentDates.start).getTime()
  );

  return (
    <div className="min-h-[60vh] pt-24 px-5 lg:pt-0">
      <div className="flex flex-col uppercase font-bold text-center lg:text-left w-full pb-8 lg:pb-4 border-b-[.5px] border-b-divider dark:border-b-divider-dark lg:border-[transparent]">
        <h2 className="text-grey-border dark:text-grey-dark">
          Welcome to{" "}
          <span className="text-jiwe-red dark:text-jiwe-yellow">
            Jiwe Esports
          </span>
        </h2>
        <h1 className="text-[#000000] dark:text-[#ffffff] text-3xl lg:text-4xl">
          Crafting Incredible
          <br className="lg:hidden" /> Play experiences
        </h1>
      </div>
      {/* Tournaments */}
      <Tournaments tournaments={tournaments} />
      {data?.live_events && data?.live_events.length > 0 && (
        <LiveOnJiwe liveEvents={data.live_events} />
      )}
    </div>
  );
}
